"use client";

import moment from "moment";

import { useTranslation } from "@/hooks/useTranslation";
import type { BookingData } from "@/types";

type Props = {
  data: BookingData;
  backToFormAction: () => void;
};

export default function BookingDoneInfo({ data, backToFormAction }: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center bg-white p-4 shadow-md">
      <div className="py-8 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="mx-auto mb-4 size-16 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mb-2 text-2xl font-bold">
          {t({ en: "Thank you!", de: "Danke!" })}
        </h3>
        <p className="mb-4 text-gray-600">
          {t({
            de: "Wir haben Ihre Nachricht erhalten und werden so schnell wie möglich antworten.",
            en: "We have received your message and will respond as soon as possible.",
          })}
        </p>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-start gap-3">
            <h3 className="text-lg font-bold text-primary-500">
              {t({ de: "Buchungsinformationen", en: "Booking Information" })}
            </h3>
            <ul className="flex flex-col items-start gap-2">
              <li>
                <span className="font-semibold">
                  {t({ de: "Anreisetag", en: "Arrival Date (MM-DD-YYYY)" })}{" "}
                  :{" "}
                </span>{" "}
                {moment(data.travelDates.arrival).format("MM-DD-YYYY")}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Abreisetag", en: "Departure Date (MM-DD-YYYY)" })}{" "}
                  :{" "}
                </span>{" "}
                {moment(data.travelDates.arrival).format("MM-DD-YYYY")}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Erwachsene", en: "Adults" })} :{" "}
                </span>{" "}
                {data.people.adults}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Kinder", en: "Children" })} :{" "}
                </span>{" "}
                {data.people.children}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Ferienwohnungen", en: "Apartment" })} /{" "}
                  {t({ de: "Zimmer", en: "Room" })} :{" "}
                </span>{" "}
                {data.accommodation.roomApartment}
              </li>
            </ul>
          </div>
          <div className="flex flex-col items-start gap-3">
            <h3 className="text-lg font-bold text-primary-500">
              {t({ de: "Ihre Informationen", en: "Your Information" })}
            </h3>
            <ul className="flex flex-col items-start gap-2">
              <li>
                <span className="font-semibold">
                  {t({ de: "Ihr Name", en: "Your Name" })} :{" "}
                </span>
                {data.customer.firstName} {data.customer.surname}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Email", en: "Email" })} :{" "}
                </span>{" "}
                {data.customer.email}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Telefonnummer", en: "Phone Number" })} :{" "}
                </span>{" "}
                {data.customer.phoneNumber}
              </li>
              <li>
                <span className="font-semibold">
                  {t({ de: "Land", en: "Country" })} :{" "}
                </span>
                {/* {data.customer.street ? `${data.customer.street}, ` : ""}
                {data.customer.houseNumber
                  ? `${data.customer.houseNumber}, `
                  : ""}
                {data.customer.postalCode
                  ? `${data.customer.postalCode}, `
                  : ""}
                {data.customer.city ? `${data.customer.city}, ` : ""} */}
                {data.customer.country}
              </li>
            </ul>
          </div>
          {data.specialRequest && (
            <div className="flex flex-col items-start gap-3">
              <h3 className="text-lg font-bold text-primary-500">
                {t({ de: "Weitere Angaben", en: "Special request" })}
              </h3>
              <p>{data.specialRequest}</p>
            </div>
          )}
        </div>
        <button
          onClick={backToFormAction}
          className="mt-5 rounded-full border-none bg-primary-500 px-8 py-4 font-bold text-gray-50 transition duration-300 hover:bg-primary-400"
        >
          {t({ de: "Buchen Sie weiter", en: "Continue booking" })}
        </button>
      </div>
    </div>
  );
}
